import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { AppDispatch } from "../state/store";
import { getAllPatientOpenSearchDocuments } from "../../patient/state/patient-thunk";
import { APIConfig } from "../../patient/components/patient-overview/open-search-patient";
import { admissionsAdvisorUtils } from "../../admissions-advisor/utils/admission-advisor-utils";
import { patientSearchResult } from "../../admissions-advisor/models/patient";

export interface UsePatientSearchOptions {
  /** Used in API config (e.g. -2 for open search). */
  paramId?: number;
  /** Min character length before running search. */
  minLength?: number;
  /** Debounce delay in ms before updating the search term used for API. */
  debounceMs?: number;
  /** Default rows per page. */
  rowsPerPageDefault?: number;
  /** Passed to API when provided. */
  addTimingRiskFilter?: boolean;
  /** Passed to API (e.g. true when user clicks Submit). */
  isClicked?: boolean;
}

const DEFAULT_OPTIONS: Required<Omit<UsePatientSearchOptions, "addTimingRiskFilter">> & Pick<UsePatientSearchOptions, "addTimingRiskFilter"> = {
  paramId: -2,
  minLength: 3,
  debounceMs: 500,
  rowsPerPageDefault: 10,
  addTimingRiskFilter: false,
  isClicked: false,
};

export interface UsePatientSearchReturn {
  /** Current value to show in the search input (sanitized). */
  inputValue: string;
  /** Update search input; sanitizes and debounced-updates the term used for API. */
  setInputValue: (raw: string) => void;
  /** Debounced search term used for the API (for derived state). */
  searchTerm: string;
  /** Result list from the last successful search. */
  patientsList: patientSearchResult[];
  /** Total count from the last successful search. */
  patientsCount: number;
  /** True while a request is in flight. */
  isLoading: boolean;
  /** Current page (0-based). */
  page: number;
  /** Current rows per page. */
  rowsPerPage: number;
  /** MUI TablePagination onPageChange. */
  handlePageChange: (event: unknown, newPage: number) => void;
  /** MUI TablePagination onRowsPerPageChange. */
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Clear search state and results (e.g. when closing the modal). */
  reset: () => void;
  /** Run search immediately with optional isClicked (e.g. Submit button). */
  searchNow: (isClicked?: boolean) => Promise<void>;
}

export function usePatientSearch(
  options: UsePatientSearchOptions = {}
): UsePatientSearchReturn {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const {
    paramId,
    minLength,
    debounceMs,
    rowsPerPageDefault,
    addTimingRiskFilter,
  } = opts;

  const dispatch = useDispatch<AppDispatch>();

  const [inputValue, setInputValueState] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [prvSearchTerm, setPrvSearchTerm] = useState("");
  const [patientsList, setPatientsList] = useState<patientSearchResult[]>([]);
  const [patientsCount, setPatientsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageDefault);

  const debouncedSetSearchTerm = useMemo(
      () =>
          debounce((value: string) => {
            setSearchTerm(value);
          }, debounceMs),
      [debounceMs]
  );

  const setInputValue = useCallback((raw: string) => {
    const sanitized = admissionsAdvisorUtils.sanitizeInput(raw);
    setInputValueState(sanitized);
    debouncedSetSearchTerm(sanitized);
  }, [debouncedSetSearchTerm]);


  useEffect(() => {
    let cancelled = false;

    if (searchTerm !== prvSearchTerm) {
      setPrvSearchTerm(searchTerm);
      setPage(0);
      return;
    }

    const fetchData = async () => {
      if (searchTerm.length < minLength || searchTerm === "") {
        setPatientsList([]);
        setPatientsCount(0);
        return;
      }

      try {
        setIsLoading(true);
        const config: APIConfig = {
          paramId,
          page: page * rowsPerPage,
          limit: rowsPerPage,
          searchTerms: searchTerm,
          isClicked: opts.isClicked ?? false,
          addTimingRiskFilter,
        };
        const response = await dispatch(
          getAllPatientOpenSearchDocuments(config)
        ).unwrap();
        if (!cancelled) {
          setPatientsList(response.entity?.data ?? []);
          setPatientsCount(response.entity?.total ?? 0);
        }
      } catch (err) {
        if (!cancelled) {
          setPatientsList([]);
          setPatientsCount(0);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    paramId,
    minLength,
    searchTerm,
    prvSearchTerm,
    page,
    rowsPerPage,
    addTimingRiskFilter,
    opts.isClicked,
  ]);

  const handlePageChange = useCallback(
    (_event: unknown, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const reset = useCallback(() => {
    setInputValueState("");
    setSearchTerm("");
    setPrvSearchTerm("");
    setPatientsList([]);
    setPatientsCount(0);
    setPage(0);
    setRowsPerPage(rowsPerPageDefault);
    debouncedSetSearchTerm.cancel();
  }, [rowsPerPageDefault, debouncedSetSearchTerm]);

  const searchNow = useCallback(
    async (isClicked = false) => {
      const term = searchTerm || inputValue;
      if (term.length < minLength) return;
      try {
        setIsLoading(true);
        const config: APIConfig = {
          paramId,
          page: page * rowsPerPage,
          limit: rowsPerPage,
          searchTerms: term,
          isClicked,
          addTimingRiskFilter,
        };
        const response = await dispatch(
          getAllPatientOpenSearchDocuments(config)
        ).unwrap();
        setPatientsList(response.entity?.data ?? []);
        setPatientsCount(response.entity?.total ?? 0);
      } catch {
        setPatientsList([]);
        setPatientsCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      paramId,
      page,
      rowsPerPage,
      searchTerm,
      inputValue,
      minLength,
      addTimingRiskFilter,
    ]
  );

  return {
    inputValue,
    setInputValue,
    searchTerm,
    patientsList,
    patientsCount,
    isLoading,
    page,
    rowsPerPage,
    handlePageChange,
    handleChangeRowsPerPage,
    reset,
    searchNow,
  };
}
